/**
 * The Feed widget's queue (feed.ts rules) wired to the CRM and the live socket:
 * a short first page on mount / settings change (shown at once, the refill
 * brings the rest), refill at 100 left, a quiet re-merge of
 * positions 11+ every 2 min (sooner while attached lists are still loading),
 * scores for pending fans as they near the front, fans who just had a chat
 * leave at once, and auto-advance in the Card view.
 *
 * The queue is remembered in memory per Scope + settings for this browser tab:
 * coming back shows it at once and a quiet refresh drops fans who no longer
 * qualify. Nothing is written to storage, so a reload starts fresh.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clearFeedSnoozes, fetchFeedPage, resolveFeed, snoozeFan } from './api';
import type { WorkspaceEvent } from './activity';
import {
  EMPTY_QUEUE,
  FIRST_BATCH,
  appendPage,
  applyVerdicts,
  chatSince,
  excludeForAppend,
  excludeForMerge,
  feedKey,
  mergePage,
  needsRefill,
  nextCard,
  pendingAhead,
  previousCard,
  queryKey,
  removeFan,
  requestSettings,
  resetWith,
  restorePage,
  shown,
  type FeedItem,
  type FeedPage,
  type FeedQueue,
  type FeedSettings,
} from './feed';

const MERGE_MS = 120_000;
const MERGE_SYNCING_MS = 20_000;
const CHAT_KINDS = new Set(['fan_message', 'team_sent', 'ai_sent']);
const REMEMBER_MAX = 20;

type Mode = 'reset' | 'append' | 'merge' | 'restore';

interface Remembered {
  queue: FeedQueue;
  lists: FeedPage['lists'];
}

/** Queues by queryKey, most recently used last (in memory only; see the header). */
const remembered = new Map<string, Remembered>();

function remember(key: string, value: Remembered) {
  remembered.delete(key);
  remembered.set(key, value);
  while (remembered.size > REMEMBER_MAX) remembered.delete(remembered.keys().next().value as string);
}

/** Signing out (or another person signing in) forgets every remembered queue. */
export function forgetFeeds() {
  remembered.clear();
}

interface Options {
  enabled: boolean;
  settings: FeedSettings;
  scope: string[] | 'all';
  onEvent: (listener: (ev: WorkspaceEvent) => void) => () => void;
  /** Auto-advance only runs in the Card view and while nothing pauses it. */
  autoRun: boolean;
  /** The chat open on the right: auto-advance waits while it is the current card. */
  openKey: string | null;
}

export function useFeed({ enabled, settings, scope, onEvent, autoRun, openKey }: Options) {
  const key = useMemo(() => queryKey(settings, scope), [settings, scope]);
  const [initial] = useState(() => (enabled ? remembered.get(key) ?? null : null));
  const [queue, setQueue] = useState<FeedQueue>(initial?.queue ?? EMPTY_QUEUE);
  const [passed, setPassed] = useState<FeedItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(!!initial);
  const [error, setError] = useState(false);
  const [lists, setLists] = useState<FeedPage['lists']>(initial?.lists ?? []);
  /** Which queryKey the queue on screen belongs to (only that one is remembered). */
  const owner = useRef<string | null>(initial ? key : null);
  const keyRef = useRef(key);
  keyRef.current = key;
  const queueRef = useRef(queue);
  queueRef.current = queue;
  const seq = useRef(0);
  const busy = useRef(false);
  const settingsRef = useRef(settings);
  settingsRef.current = settings;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  const request = useCallback(async (mode: Mode) => {
    const id = ++seq.current;
    const q = queueRef.current;
    const forKey = keyRef.current;
    const exclude = mode === 'reset' || mode === 'restore' ? q.seen : mode === 'append' ? excludeForAppend(q) : excludeForMerge(q);
    busy.current = true;
    if (mode === 'reset' || mode === 'append') setLoading(true);
    try {
      const page = await fetchFeedPage({
        settings: requestSettings(settingsRef.current) as unknown as Record<string, unknown>,
        chat_since: chatSince(settingsRef.current.chatPeriod, Date.now()),
        exclude,
        scope: scopeRef.current,
        ...(mode === 'reset' ? { limit: FIRST_BATCH } : {}),
      });
      if (id !== seq.current) return;
      const apply = mode === 'reset' ? resetWith : mode === 'append' ? appendPage : mode === 'restore' ? restorePage : mergePage;
      setQueue((cur) => apply(cur, page));
      if (mode === 'reset') owner.current = forKey;
      setLists(page.lists);
      setError(false);
    } catch {
      if (id === seq.current) setError(true);
    } finally {
      if (id === seq.current) {
        busy.current = false;
        setLoading(false);
        setLoaded(true);
      }
    }
  }, []);

  // First page, and a fresh ranking whenever settings or scope change; a remembered
  // queue for these settings shows at once and is refreshed quietly instead.
  useEffect(() => {
    if (!enabled) return;
    const saved = owner.current === key ? null : remembered.get(key);
    if (saved) {
      owner.current = key;
      queueRef.current = saved.queue;
      setQueue(saved.queue);
      setLists(saved.lists);
      setLoaded(true);
      setError(false);
    }
    void request(owner.current === key ? 'restore' : 'reset');
  }, [enabled, key, request]);

  // Remember the queue on screen under the settings it was built for.
  useEffect(() => {
    if (enabled && loaded && owner.current === key) remember(key, { queue, lists });
  }, [enabled, loaded, key, queue, lists]);

  // Refill when 100 cards are left.
  useEffect(() => {
    if (enabled && loaded && !busy.current && !error && needsRefill(queue)) void request('append');
  }, [enabled, loaded, error, queue, request]);

  // Quiet re-merge of positions 11+ (new list members, new scores, fans who stopped qualifying).
  const syncing = lists.some((l) => l.status === 'syncing' || l.status === 'refreshing');
  useEffect(() => {
    if (!enabled || !loaded) return;
    const timer = window.setInterval(() => {
      if (!busy.current && document.visibilityState === 'visible') void request('merge');
    }, syncing ? MERGE_SYNCING_MS : MERGE_MS);
    return () => window.clearInterval(timer);
  }, [enabled, loaded, syncing, request]);

  // Scores for pending fans as they come within 30 of the front.
  const asked = useRef(new Set<string>());
  useEffect(() => {
    if (!enabled) return;
    const ahead = pendingAhead(queue).filter((i) => !asked.current.has(feedKey(i)));
    if (!ahead.length) return;
    for (const i of ahead) asked.current.add(feedKey(i));
    resolveFeed(requestSettings(settingsRef.current) as unknown as Record<string, unknown>, ahead.map((i) => ({ account_id: i.account_id, fan_id: i.fan_id })))
      .then((verdicts) => {
        // A fan the CRM couldn't score stays out of view rather than slipping a filter.
        const answered = new Set(verdicts.map(feedKey));
        const unanswered = ahead.filter((i) => !answered.has(feedKey(i))).map((i) => ({ account_id: i.account_id, fan_id: i.fan_id, bp: i.bp, temp: i.temp, verdict: 'fail' }));
        setQueue((q) => applyVerdicts(q, [...verdicts, ...unanswered]));
      })
      .catch(() => {
        for (const i of ahead) asked.current.delete(feedKey(i));
      });
  }, [enabled, queue]);

  // A fan who just had a chat (anyone, either way) leaves the queue, frozen head included.
  useEffect(
    () =>
      onEvent((ev) => {
        if (!CHAT_KINDS.has(ev.kind)) return;
        const k = feedKey(ev);
        if (queueRef.current.items.some((i) => feedKey(i) === k)) setQueue((q) => removeFan(q, k, false));
      }),
    [onEvent],
  );

  const next = useCallback(() => {
    const cur = shown(queueRef.current)[0];
    if (!cur) return;
    setPassed((p) => [...p.slice(-49), cur]);
    setQueue((q) => nextCard(q));
  }, []);

  const passedRef = useRef(passed);
  passedRef.current = passed;
  const previous = useCallback(() => {
    // Computed outside the updaters: state updaters must stay pure (StrictMode runs them twice).
    const out = previousCard(queueRef.current, passedRef.current);
    setQueue(out.queue);
    setPassed(out.passed);
  }, []);

  const skip = useCallback((item: FeedItem) => {
    setQueue((q) => removeFan(q, feedKey(item)));
    snoozeFan(item.account_id, item.fan_id, settingsRef.current.snoozeHours).catch(() => undefined);
  }, []);

  const bringBack = useCallback(async () => {
    try {
      await clearFeedSnoozes();
    } catch {
      /* the reset below still shows passed fans again */
    }
    setPassed([]);
    setQueue(EMPTY_QUEUE);
    queueRef.current = EMPTY_QUEUE;
    void request('reset');
  }, [request]);

  // Auto-advance (Card view): restart the clock whenever the current card changes.
  const current = shown(queue)[0];
  const currentKey = current ? feedKey(current) : null;
  const running = autoRun && !!currentKey && currentKey !== openKey && settings.autoAdvance > 0;
  const [cycle, setCycle] = useState(0);
  useEffect(() => {
    if (!running) return;
    setCycle((c) => c + 1);
    const timer = window.setTimeout(next, settings.autoAdvance * 1000);
    return () => window.clearTimeout(timer);
  }, [running, currentKey, settings.autoAdvance, next]);

  // Same array until the queue changes, so the Feed's cards are not rebuilt on every page render.
  const items = useMemo(() => shown(queue), [queue]);

  return {
    queue,
    items,
    loading,
    loaded,
    error,
    lists,
    /** Changes every time the auto-advance clock restarts (the progress bar keys on it). */
    cycle,
    running,
    next,
    previous,
    skip,
    bringBack,
    retry: () => void request('reset'),
  };
}
